<template>
    <div class="info-step">
        <form class="info-form">
            <div class="first-row">
                <div class="inputs">
                    <div class="first-name-wrapper">
                        <label for="first_name" class="first-name-label">Name*</label>
                        <input
                            v-model="$v.userData.first_name.$model"
                            @keydown="$v.userData.first_name.$touch"
                            id="first_name"
                            class="second-name-input"
                            type="text"
                            placeholder="First name"
                        />
                        <span
                            v-if="
                                isMobile &&
                                $v.userData.first_name.$dirty &&
                                !$v.userData.first_name.required
                            "
                            class="error-form-message"
                        >
                            First name is required.
                        </span>
                    </div>
                    <div class="last-name-wrapper">
                        <label for="last_name" class="last-name-label">Last</label>
                        <input
                            v-model="$v.userData.last_name.$model"
                            @keydown="$v.userData.last_name.$touch"
                            id="last_name"
                            class="last-name-input"
                            type="text"
                            placeholder="Last name"
                        />
                    </div>
                </div>
                <div class="err-msgs">
                    <span
                        v-if="
                            !isMobile &&
                            $v.userData.first_name.$dirty &&
                            !$v.userData.first_name.required
                        "
                        class="error-form-message"
                    >
                        First name is required.
                    </span>
                    <span v-else class="error-form-message visible-hidden"></span>
                    <span
                        v-if="$v.userData.last_name.$dirty && !$v.userData.last_name.required"
                        class="error-form-message"
                    >
                        Last name is required.
                    </span>
                </div>
            </div>
            <div class="second-row">
                <div class="password-wrapper">
                    <label for="password" class="password-label">Password*</label>
                    <input
                        v-model="$v.userData.password.$model"
                        @keydown="$v.userData.password.$touch"
                        id="password"
                        :type="showPassword ? 'text' : 'password'"
                        class="password-input"
                        placeholder="8+ characters, include both letters and numbers"
                    />
                    <i
                        class="fa"
                        :class="showPassword ? 'fa-eye' : 'fa-eye-slash'"
                        aria-hidden="true"
                        @click="showPassword = !showPassword"
                    ></i>
                </div>
                <span
                    class="error-form-message"
                    v-if="$v.userData.password.$dirty && !$v.userData.password.required"
                >
                    Password is required.
                </span>
                <span
                    class="error-form-message"
                    v-if="$v.userData.password.$dirty && !$v.userData.password.minLength"
                >
                    Please enter at least 8 characters.
                </span>
                <span
                    class="error-form-message"
                    v-if="$v.userData.password.$dirty && !$v.userData.password.maxLength"
                >
                    Please enter less than 25 characters.
                </span>
                <span
                    class="error-form-message"
                    v-if="$v.userData.password.$dirty && !$v.userData.password.containsLetter"
                >
                    Please use at least one letter in your password.
                </span>
                <span
                    class="error-form-message"
                    v-if="$v.userData.password.$dirty && !$v.userData.password.containsNumber"
                >
                    Please use at least one number in your password.
                </span>
            </div>
            <p class="third-row terms-msg">
                By continuing, I agree to Hedlyner's
                <a href="/terms-of-service" target="_blank">Terms of Service</a> and
                <a href="/privacy-policy" target="_blank">Privacy Policy</a>.
            </p>
        </form>
        <div class="actions">
            <button @click="nextStep" :disabled="$v.$invalid" class="cta-btn">
                Claim Your Profile
            </button>
        </div>
    </div>
</template>
<script>
    import { useArtistClaimProfile } from '@/composables/features/artist-claim-profile/useArtistClaimProfile'
    import useCheckScreen from '@/composables/useCheckScreen.js'
    import { required, minLength, maxLength } from 'vuelidate/lib/validators'

    export default {
        name: 'InfoStep',
        setup() {
            const { userData, sendData, showPassword, updateStep } = useArtistClaimProfile()
            const { isMobile } = useCheckScreen()

            return {
                isMobile,

                userData,
                sendData,
                showPassword,
                updateStep
            }
        },
        validations: {
            userData: {
                first_name: { required },
                last_name: { required },
                password: {
                    required,
                    minLength: minLength(8),
                    maxLength: maxLength(25),
                    containsNumber: function (value) {
                        return /[0-9]/.test(value)
                    },
                    containsLetter: function (value) {
                        return /[a-zA-Z]/.test(value)
                    }
                }
            }
        },
        methods: {
            async nextStep() {
                if (
                    this.userData.first_name &&
                    this.userData.last_name &&
                    this.userData.password &&
                    this.formValid()
                ) {
                    if (await this.sendData()) {
                        this.updateStep('match-step')
                    } else {
                        this.updateStep('mismatch-step')
                    }
                }
            },
            formValid() {
                this.$v.$touch()
                return !this.$v.$invalid
            }
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    $mobileMax: 600px;

    @media screen and (max-width: $mobileMax) {
        .info-form {
            padding-left: 0px !important;
            padding-right: 0px !important;
            padding-bottom: 40px !important;
            border-bottom: none !important;

            .inputs {
                flex-direction: column !important;
            }
        }
        .actions {
            padding-left: 0px !important;
            padding-right: 0px !important;

            .cta-btn {
                width: 100%;
            }
        }
        .visible-hidden {
            display: none;
        }
    }
    a {
        color: inherit;
    }
    button {
        border: none;
    }
    p {
        margin-bottom: 0px;
    }

    label {
        color: #8b8b8b;
        @include base-font-12;
        font-weight: 650;
        line-height: 135%;
        letter-spacing: 0.12px;
    }
    input {
        width: 100%;
        padding: 12px 16px 16px 16px;
        border-radius: 12px;
        border: 1px solid #8b8b8b;
        background: #262626;
        color: #dedede;
        @include base-font-16;
        font-weight: 550;
        line-height: 125%;
    }
    input:focus {
        border: 1px solid #cdeb09;
    }
    input:active {
        border: 1px solid #cdeb09;
    }
    input::placeholder {
        overflow: hidden;
        color: #646464;
        text-overflow: ellipsis;
        white-space: nowrap;
        @include base-font-16;
        font-weight: 550;
        line-height: 125%;
    }
    .error-form-message {
        flex: 1 1 0px;
        color: #ed3c0d;
        font-family: Instrument Sans;
        font-size: 10px;
        font-style: normal;
        font-weight: 600;
        line-height: 130%;
        letter-spacing: 0.14px;
    }
    .visible-hidden {
        visibility: hidden;
    }

    .info-step {
        display: flex;
        flex-direction: column;
        gap: 0px;
        align-items: center;
        justify-content: center;
        width: 100%;

        .info-form {
            padding-bottom: 42px;
            border-bottom: 1px solid #333333;
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 24px;
            align-items: flex-start;
            justify-content: center;
            padding-left: 56px;
            padding-right: 56px;

            .first-row {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 5px;
                width: 100%;

                .inputs {
                    display: flex;
                    flex-direction: row;
                    align-items: flex-start;
                    justify-content: center;
                    gap: 12px;
                    width: 100%;

                    .first-name-wrapper,
                    .last-name-wrapper {
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-items: flex-start;
                        gap: 6px;

                        .last-name-label {
                            visibility: hidden;
                        }
                    }
                }

                .err-msgs {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    gap: 12px;
                }
            }

            .second-row {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 5px;

                .password-wrapper {
                    position: relative;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 6px;

                    .fa {
                        cursor: pointer;
                        color: #acacac;
                        position: absolute;
                        bottom: 25%;
                        right: 10px;
                    }
                }
            }
            .terms-msg {
                color: var(--Neutral-500, #646464);
                @include base-font-12;
                font-weight: 650;
                line-height: 135%;
                letter-spacing: 0.12px;
            }
        }
        .actions {
            width: 100%;
            padding: 16px 24px;

            .cta-btn {
                margin-left: auto;
                display: flex;
                justify-content: center;
                align-items: center;
                display: flex;
                padding: 12px 32px;
                border-radius: 120px;
                background: #cdeb09;
                color: #000;
                @include base-font-18;
                line-height: 133%;
            }
            .cta-btn:disabled {
                opacity: 0.5;
            }
        }
    }
</style>
